/**
 * Group entity validation rules.
 *
 * Aligns with backend GroupValidator requirements:
 *   - DisplayName, AccountName, Scope, Type are required for SCIM-sourced groups
 *   - Filter/ManagedType consistency
 *   - External groups are AD-managed (informational)
 */

import { getEntityAttrString } from './ReusableValidation';
import type { ValidationWarning } from './ReusableValidation';

/**
 * Returns validation warnings for Group entity data (view or form).
 * Call from both EntityDetailDialog (view) and GenericEntityUpsertDialog (edit).
 */
export function getGroupWarnings(data: Record<string, any>): ValidationWarning[] {
    const warnings: ValidationWarning[] = [];
    const managedType = getEntityAttrString(data, 'ManagedType').toLowerCase();
    const filterVal = getEntityAttrString(data, 'Filter');
    const isEnumerated = managedType === 'enumerated';
    const isManual = managedType === 'manual';
    const isExternal = managedType === 'external';

    const displayName = getEntityAttrString(data, 'DisplayName');
    const accountName = getEntityAttrString(data, 'AccountName');
    const scope = getEntityAttrString(data, 'Scope');
    const type = getEntityAttrString(data, 'Type');

    if (!displayName.trim()) {
        warnings.push({
            message: 'Display Name is required.',
            severity: 'warning',
            attributeDisplayNames: ['DisplayName']
        });
    }

    if (!accountName.trim()) {
        warnings.push({
            message: 'Account Name is required for group provisioning.',
            severity: 'warning',
            attributeDisplayNames: ['AccountName']
        });
    }

    if (!scope.trim()) {
        warnings.push({
            message: 'Scope is required (Global, Local, or Universal).',
            severity: 'warning',
            attributeDisplayNames: ['Scope']
        });
    }

    if (!type.trim()) {
        warnings.push({
            message: 'Type is required (Security or Distribution).',
            severity: 'warning',
            attributeDisplayNames: ['Type']
        });
    }

    if (isExternal) {
        warnings.push({
            message: 'This group is managed by Active Directory. Changes should be made in AD.',
            severity: 'info',
            attributeDisplayNames: ['ManagedType']
        });
    }

    if (isManual && filterVal.trim()) {
        warnings.push({
            message: 'Filter must be empty when Managed Type is Manual.',
            severity: 'warning',
            attributeDisplayNames: ['Filter', 'ManagedType']
        });
    }

    if (!isEnumerated && filterVal) {
        warnings.push({
            message: 'Filter has no effect when Managed Type is not Enumerated. Set Managed Type to Enumerated for the filter to define group membership.',
            severity: 'warning',
            attributeDisplayNames: ['Filter', 'ManagedType']
        });
    }

    return warnings;
}
